import React, { useEffect, useState } from "react";
import { UserService, Campaign } from "../../services/UserService";
import DashboardComponent from "../../components/Dashboard";
import Chatbot from "../../components/Chatbot";
import HeaderUser from "../../components/HeaderUser";

const Dashboard: React.FC = () => {
  const cachedCampaigns = (() => {
    try {
      const raw = localStorage.getItem("cachedCampaigns");
      return raw ? (JSON.parse(raw) as Campaign[]) : null;
    } catch {
      return null;
    }
  })();
  const cachedSelectedId = (() => {
    try {
      const raw = localStorage.getItem("selectedCampaignId");
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  })();

  const [user, setUser] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(cachedCampaigns);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(() => {
    if (cachedCampaigns && cachedCampaigns.length > 0) {
      if (cachedSelectedId) {
        const found = cachedCampaigns.find((c) => c.id === cachedSelectedId);
        if (found) return found;
      }
      return cachedCampaigns[0];
    }
    return null;
  });
  const [loading, setLoading] = useState(!cachedCampaigns);
  const [error, setError] = useState<string | null>(null);
  const [isChatbotVisible, setIsChatbotVisible] = useState(true);
  const fallbackCampaign: Campaign = {
    id: 0,
    name: "Campanha Teste",
    dashboard_url: "about:blank",
    platform_id: "TESTE",
    matomo_id: "TESTE",
    primary_color: "#0b3d91",
    secondary_color: "#ffffff",
    chatbot_enabled: true,
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await UserService.getUserWithCamaigns();
        setUser(userData);
        const userCampaigns = userData.campaigns ?? [];
        const baseCampaigns = userCampaigns.length > 0 ? userCampaigns : [fallbackCampaign];
        setCampaigns(baseCampaigns);
        // evita piscar: só atualiza cache se a url mudou, e mantém a mesma seleção quando possível
        const cached = localStorage.getItem("cachedCampaigns");
        const cachedParsed: Campaign[] | null = cached ? JSON.parse(cached) : null;
        const cachedDash = cachedParsed?.find((c) => c.id === selectedCampaign?.id)?.dashboard_url;
        const incomingDash = baseCampaigns.find((c) => c.id === selectedCampaign?.id)?.dashboard_url;
        if (!cachedDash || cachedDash !== incomingDash) {
          localStorage.setItem("cachedCampaigns", JSON.stringify(baseCampaigns));
        }
        const savedCampaignId = localStorage.getItem("selectedCampaignId");

        if (baseCampaigns && baseCampaigns.length > 0) {
          if (savedCampaignId) {
            const savedCampaign = baseCampaigns.find(
              (campaign: Campaign) => campaign.id === parseInt(savedCampaignId)
            );
            if (savedCampaign) {
              setSelectedCampaign(savedCampaign);
            } else {
              setSelectedCampaign(baseCampaigns[0]);
              localStorage.setItem("selectedCampaignId", baseCampaigns[0].id.toString());
            }
          } else {
            setSelectedCampaign(baseCampaigns[0]);
            localStorage.setItem("selectedCampaignId", baseCampaigns[0].id.toString());
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Erro ao carregar dados do usuário");
      } finally {
        setLoading(false);
      }
    };

    if (!campaigns) {
      setLoading(true);
      fetchUserData();
    } else {
      // atualiza em background sem forçar loading/flicker
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCampaignChange = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    localStorage.setItem("selectedCampaignId", campaign.id.toString());
  };

  const onFocusMode = (value: boolean) => {
    setIsChatbotVisible(value);
  };

  if (loading && !campaigns) {
    return <div className="flex justify-center items-center h-screen text-[#666] text-lg">Carregando...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-600 text-base text-center p-5">{error}</div>;
  }

  if (!user && (!campaigns || campaigns.length === 0) && !loading) {
    return <div className="flex justify-center items-center h-screen text-red-600 text-base text-center p-5">Nenhuma campanha encontrada para este usuário.</div>;
  }

  const isTeste = selectedCampaign?.name === "Campanha Teste" || selectedCampaign?.id === 0;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <HeaderUser
        campaigns={campaigns}
        selectedCampaign={selectedCampaign}
        onSelectCampaign={handleCampaignChange}
        onFocusMode={onFocusMode}
      />
      <div className="flex flex-1 gap-5 overflow-hidden p-5 relative z-[1] flex-col lg:flex-row lg:flex-nowrap lg:items-stretch">
        {selectedCampaign && (
          <div
            className={`flex-[2] min-w-0 bg-white rounded-lg shadow-sm overflow-hidden p-5 transition-colors ${
              isTeste ? "bg-transparent shadow-none p-0" : ""
            }`}
          >
            {isTeste ? (
              <div
                className="w-full h-full flex items-center justify-center text-3xl font-bold text-[#f8fbff] rounded-xl relative overflow-hidden"
                style={{ background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08), transparent 45%), radial-gradient(circle at 80% 0%, rgba(154,208,255,0.14), transparent 38%), linear-gradient(135deg, #0b3d91, #0f52c1 55%, #0b3d91)', boxShadow: '0 20px 60px rgba(8,32,96,0.4), inset 0 0 0 1px rgba(255,255,255,0.06)' }}
              >
                Campanha Teste
              </div>
            ) : (
              <DashboardComponent dashboardUrl={selectedCampaign.dashboard_url} />
            )}
          </div>
        )}
        {isChatbotVisible && selectedCampaign?.chatbot_enabled && (
          <div className="flex-shrink-0 min-w-[320px] max-w-[420px] bg-white rounded-lg p-5 shadow-sm overflow-hidden h-[250px] min-h-[250px] sm:h-[300px] sm:min-h-[300px] md:h-auto md:min-h-0">
            <Chatbot campaignId={selectedCampaign.id} primaryColor={selectedCampaign.primary_color} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
